import { useEffect, useState } from 'react';
import { getLedgerSummary } from '../../api/adminFinanceApi';

const formatMoney = (currency, paise) => {
  const amount = Number(paise || 0) / 100;
  if (!Number.isFinite(amount)) return `${currency || ''} 0`.trim();
  return `${currency || ''} ${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

const SummaryCard = ({ label, value, hint }) => (
  <div className="admin-card rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-2 text-xl font-semibold text-slate-900">{value}</div>
    {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
  </div>
);

const AdminFinanceSummary = ({ token }) => {
  const [summary, setSummary] = useState(null);
  const [currency, setCurrency] = useState('INR');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (nextCurrency = currency) => {
    setLoading(true);
    setError('');
    try {
      const data = await getLedgerSummary(token, { currency: nextCurrency });
      setSummary(data);
      setCurrency(data.currency || nextCurrency);
    } catch (err) {
      setError(err.message || 'Failed to load ledger summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(currency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Finance</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Ledger Summary</h1>
          <p className="text-sm text-slate-600">Snapshot of platform balances and liabilities.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
          >
            <option value="INR">INR</option>
          </select>
          <button
            type="button"
            onClick={() => load(currency)}
            className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      {loading && <div className="mt-4 text-sm text-slate-500">Loading...</div>}

      {!loading && summary && (
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <SummaryCard label="Platform Cash" value={formatMoney(summary.currency, summary.cash)} />
          <SummaryCard label="Host Payables" value={formatMoney(summary.currency, summary.payables)} />
          <SummaryCard label="Fees Earned" value={formatMoney(summary.currency, summary.fees)} />
          <SummaryCard
            label="Pending Refunds"
            value={formatMoney(summary.currency, summary.pendingRefunds?.amount)}
            hint={`${summary.pendingRefunds?.count || 0} pending`}
          />
          <SummaryCard
            label="Payouts Scheduled"
            value={formatMoney(summary.currency, summary.payoutsScheduled?.amount)}
            hint={`${summary.payoutsScheduled?.count || 0} scheduled`}
          />
          <SummaryCard label="Gateway Fees" value={formatMoney(summary.currency, summary.gatewayFees)} />
          <SummaryCard label="Refunds Payable" value={formatMoney(summary.currency, summary.refundsPayable)} />
          <SummaryCard label="Payouts Out" value={formatMoney(summary.currency, summary.payoutsOut)} />
        </div>
      )}
    </div>
  );
};

export default AdminFinanceSummary;
